import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Slide, ZoomProps, Stack, Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { LoadingButton, PoweredByWatermark, Typography, SizingMultiplier, Paper, NoStoresFound } from '@ocx/ui';
import { IStore } from '@ocx-app/modules/stores/types';
import { InfoIcon, MapIcon } from '@ocx/ui-icons';

import { StoreCard } from './store-card/store-card.component';
import { messages } from '../store-locator.messages';
import { ControlButton } from './control-button.component';

// Zoom component does not have "className" property
const zoomProps: Omit<ZoomProps, 'children'> = {
  style: {
    transitionDelay: `200ms`,
  },
};

type ListViewProps = {
  loading: boolean;
  stores: IStore[];
  onMapViewClick(): void;
  onStoreClick(storeId: string): void;
  isLocationGranted: boolean;
  onLoadMoreClick(): void;
  isLoadMoreAvailable: boolean;
  isNoStoresFound: boolean;
};

export const ListView: React.FC<ListViewProps> = (props) => {
  const {
    stores,
    onMapViewClick,
    onStoreClick,
    loading,
    isLocationGranted,
    onLoadMoreClick,
    isLoadMoreAvailable,
    isNoStoresFound,
  } = props;

  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => setOpen(false), []);
  useEffect(() => {
    // We need to open slider AFTER component is mounted in case of animation issue (no animation if mounted with 'open' state)
    setOpen(true);
  }, []);

  const storesList = useMemo(
    () =>
      stores.map((store) => (
        <StoreCard
          addressLine1={store.addressLine1}
          addressLine2={store.addressLine2}
          distance={isLocationGranted ? store.distance : null}
          fuelPriceState={store.fuelPriceState}
          priceGroups={store.priceGroups}
          onCardClick={onStoreClick}
          directionLink={isLocationGranted ? store.directionLink : null}
          storeId={store.id}
          key={store.id}
        />
      )),
    [isLocationGranted, onStoreClick, stores],
  );

  return (
    <Slide in={open} direction="up" onExited={onMapViewClick}>
      <Paper
        p={0}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          overflowY: 'auto',
          backgroundColor: 'background.default',
          zIndex: (theme) => theme.zIndex.fab + 1,
        }}
        square>
        <Stack spacing={SizingMultiplier.sm} py={10} px={SizingMultiplier.sm}>
          {isNoStoresFound && <NoStoresFound open />}
          {storesList}
          {isLoadMoreAvailable && (
            <LoadingButton loading={loading} onClick={onLoadMoreClick} variant="outlined">
              Load more
            </LoadingButton>
          )}

          {!isNoStoresFound && !isLoadMoreAvailable && (
            <Typography variant="caption" color="textSecondary" display="flex" alignContent="center">
              <InfoIcon sx={{ color: 'text.disabled', mr: SizingMultiplier.xs, width: 16, height: 16 }} />
              <FormattedMessage {...messages['storeLocator:listView:adjustLocationToSeeMoreStoresMessage']} />
            </Typography>
          )}

          <PoweredByWatermark />
        </Stack>

        <Box position="fixed" bottom={0} right={0} pb={SizingMultiplier.sm}>
          <ControlButton
            zoomProps={zoomProps}
            icon={<MapIcon sx={{ width: 20, height: 19 }} />}
            onClick={handleClose}
          />
        </Box>
      </Paper>
    </Slide>
  );
};
